import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Users, Award, Clock, Shield, Target } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCanonical } from '../../hooks/useCanonical';
import taxyuktiLogo from '../images/taxyukti_logo.png';

const AboutUs: React.FC = () => {
  // Add canonical tag for about page
  useCanonical('/about');
  const stats = [
    { number: '500+', label: 'Happy Clients', icon: Users },
    { number: '15+', label: 'Years Experience', icon: Clock },
    { number: '2000+', label: 'Cases Handled', icon: Award },
    { number: '90%', label: 'Success Rate', icon: Target }
  ];

  const values = [
    {
      title: 'Excellence',
      description: 'We strive for excellence in every aspect of our service delivery, ensuring the highest quality standards.',
      icon: Award
    },
    {
      title: 'Integrity',
      description: 'We maintain the highest ethical standards and transparency in all our dealings with clients.',
      icon: Shield
    },
    {
      title: 'Innovation',
      description: 'We continuously innovate and adopt new technologies to provide better solutions to our clients.',
      icon: Target
    },
    {
      title: 'Client Focus',
      description: 'Our clients are at the center of everything we do, and we prioritize their success above all.',
      icon: Users
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Company Overview */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[rgb(19,47,113)] mb-6">
                About Us
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At TaxYukti, we deliver premium-quality GST solutions at a reasonable cost, providing true value for money.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our team of experienced Chartered Accountants, GST Experts, and Lawyers offers end-to-end services, including compliance, advisory, representation, and litigation support.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Combining technical excellence with personalized attention, we ensure accuracy, timeliness, and practical solutions that keep businesses compliant while optimizing their tax positions.
              </p>
            </div>
            <div className="text-center">
              <img
                src={taxyuktiLogo}
                alt="TaxYukti Logo"
                className="w-64 h-64 mx-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="text-white" size={32} />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-primary-lighter font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[rgb(19,47,113)] mb-4">
              Our Values
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg text-center">
                <div className="bg-primary-lighter w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="text-primary" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Work With Us?
          </h2>
          <p className="text-xl text-primary-lighter mb-8 max-w-2xl mx-auto">
            Let our expert team help you navigate the GST landscape with confidence
          </p>
          <div className="flex justify-center">
            <Link
              to="/login"
              className="border-2 border-white text-white hover:bg-white hover:text-[#19679E] hover:border-[#19679E] px-8 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Ask Your GST Queries for Free
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
